export const EMBED_MESSAGE_PREFIX = 'mtn-chat:';

export type EmbedMessageType =
    | 'ready'
    | 'open'
    | 'close'
    | 'shutdown'
    | 'error'
    | 'update'
    | 'external_url';

export interface EmbedMessage {
    type: `${typeof EMBED_MESSAGE_PREFIX}${EmbedMessageType}`;
    payload?: Record<string, unknown>;
}

const parseOrigin = (value: string, defaultProtocol: 'http:' | 'https:' = 'https:'): string | null => {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    let candidate = trimmed;
    try {
        candidate = decodeURIComponent(trimmed);
    } catch {
        candidate = trimmed;
    }

    // Absolute http(s) URLs. Values like "localhost:3000" can parse as protocol "localhost:"
    // in URL(), so only accept explicit http(s) schemes here.
    if (/^https?:\/\//i.test(candidate)) {
        try {
            const url = new URL(candidate);
            return url.origin;
        } catch {
            return null;
        }
    }

    if (/[/?#\\]/.test(candidate) || candidate.includes('://')) {
        return null;
    }

    try {
        const url = new URL(`${defaultProtocol}//${candidate}`);
        if (!url.hostname) {
            return null;
        }
        return url.origin;
    } catch {
        return null;
    }
};

export const getEmbedParentOrigin = (): string | null => {
    const searchParams = new URLSearchParams(window.location.search);
    const parentOriginParam = searchParams.get('parentOrigin');
    if (parentOriginParam) {
        const protocol = searchParams.get('parentProto') === 'http' ? 'http:' : 'https:';
        const parsedParamOrigin = parseOrigin(parentOriginParam, protocol);
        if (parsedParamOrigin) {
            return parsedParamOrigin;
        }
    }

    const ancestorOrigins = window.location.ancestorOrigins;
    if (ancestorOrigins && ancestorOrigins.length > 0) {
        return ancestorOrigins[0];
    }

    if (document.referrer) {
        return parseOrigin(document.referrer);
    }

    return null;
};

export const isAllowedEmbedParentMessage = (origin: string): boolean => {
    const expectedOrigin = getEmbedParentOrigin();
    return expectedOrigin !== null && origin === expectedOrigin;
};

export const isEmbedContext = (): boolean => {
    if (window.parent !== window) {
        return true;
    }

    return new URLSearchParams(window.location.search).get('embed') === 'true';
};

export const notifyEmbedParent = (
    event: EmbedMessageType,
    payload: Record<string, unknown> = {}
): void => {
    if (window.parent === window) {
        return;
    }

    const parentOrigin = getEmbedParentOrigin();
    if (!parentOrigin) {
        return;
    }

    const message: EmbedMessage = {
        type: `${EMBED_MESSAGE_PREFIX}${event}`,
        payload
    };

    window.parent.postMessage(message, parentOrigin);
};

export const parseEmbedMessage = (data: unknown): { event: EmbedMessageType; payload: Record<string, unknown> } | null => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const message = data as EmbedMessage;
    if (typeof message.type !== 'string' || !message.type.startsWith(EMBED_MESSAGE_PREFIX)) {
        return null;
    }

    const event = message.type.slice(EMBED_MESSAGE_PREFIX.length) as EmbedMessageType;

    return {
        event,
        payload: message.payload || {}
    };
};
